import React, { useState, useEffect } from "react";

import axios from "../../services/axios";

import {
  ThemeProvider,
  createTheme,
  Box,
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Fab,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  MenuItem,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  PlayArrow as PlayIcon,
  FileUpload as UploadIcon,
} from "@mui/icons-material";

// ------------------------
// CONFIGURACIÓN DE CATEGORÍAS
// ------------------------

const categorias = {
  "Atractivos Turísticos": [
    "Históricos",
    "Religiosos",
    "Culturales",
    "Naturales",
  ],

  "Circuitos Turísticos": [
    "Histórico",
    "Comercial",
  ],

  Hoteles: [
    "1 estrella",
    "2 estrellas",
    "3 estrellas",
  ],

  Hostales: [
    "1 estrella",
    "2 estrellas",
    "3 estrellas",
  ],

  Gastronomía: [
    "Pollería",
    "Cevichería",
    "Chifa",
    "Pizzería",
    "Cafetería",
    "Pastelería",
    "Parrillada / Grill",
    "Vegetariano",
    "Restobar",
    "Asiática",
    "Kankachos",
    "Heladerías",
    "Quinta Restaurante",
    "Platos Típicos",
    "Trucherias",
  ],

  "Agencia de Viajes": [
    "Todos",
  ],

  "Guías Turísticos": [
    "Todos",
  ],

  Artesanías: [
    "Todos",
  ],

  "Centros Comerciales": [
    "Plazas",
    "Mercados",
  ],

  "Transporte Publico": [
    "Aeropuerto",
    "Terminales",
    "Taxis",
  ],
};

const theme = createTheme({
  palette: {
    primary: {
      main: "#0B74DA",
    },

    background: {
      default: "#F5F7FA",
      paper: "#ffffff",
    },
  },

  typography: {
    h4: {
      fontWeight: 700,
    },
  },
});

export default function Destinos() {

  // =========================================================
  // ESTADOS PRINCIPALES
  // =========================================================

  const [destinos, setDestinos] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // =========================================================
  // ESTADOS DE DIÁLOGOS
  // =========================================================

  const [formOpen, setFormOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [editing, setEditing] = useState(false);

  const [currentIdToDelete, setCurrentIdToDelete] = useState(null);

  // =========================================================
  // ESTADOS DE ARCHIVOS
  // =========================================================

  // Archivo de la imagen principal
  const [selectedFile, setSelectedFile] = useState(null);

  // Archivos NUEVOS de galería
  const [galleryFiles, setGalleryFiles] = useState([]);

  // Todas las imágenes que se muestran en la galería
  const [gallery, setGallery] = useState([]);

  // IDs de imágenes EXISTENTES que el usuario eliminó
  const [deletedGalleryIds, setDeletedGalleryIds] = useState([]);

  // =========================================================
  // NOTIFICACIÓN
  // =========================================================

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // =========================================================
  // FORMULARIO
  // =========================================================

  const [form, setForm] = useState({
    id: "",
    nombre: "",
    ciudad: "",
    direccion: "",
    categoria: "",
    subcategoria: "",
    descripcion: "",
    foto: "",
    video: "",
    latitud: "",
    longitud: "",
  });

  // =========================================================
  // CATEGORÍAS
  // =========================================================

  const categoriaOptions = Object.keys(categorias);

  const subcategoriaOptions =
    categorias[form.categoria] || [];

  // =========================================================
  // CARGAR DESTINOS
  // =========================================================

  useEffect(() => {
    fetchDestinos();
  }, []);

  async function fetchDestinos() {

    try {

      const res = await axios.get("/destinos");

      setDestinos(res.data || []);
      setFiltered(res.data || []);

    } catch (err) {

      console.error(
        "Error al obtener destinos",
        err
      );

    }
  }

  // =========================================================
  // NOTIFICACIONES
  // =========================================================

  const handleCloseNotification = () => {

    setNotification({
      ...notification,
      open: false,
    });

  };

  // =========================================================
  // IMAGEN PRINCIPAL
  // =========================================================

  function handleFileChange(e) {

    const file = e.target.files[0];

    if (file) {

      setSelectedFile(file);

      setForm((prev) => ({
        ...prev,
        foto: URL.createObjectURL(file),
      }));

    }

  }

  // =========================================================
  // INPUTS
  // =========================================================

  function handleInputChange(e) {

    const {
      name,
      value,
    } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

  }

  // =========================================================
  // NUEVO DESTINO
  // =========================================================

  function openNew() {

    setSelectedFile(null);

    setGalleryFiles([]);

    setGallery([]);

    // MUY IMPORTANTE:
    // limpiar imágenes eliminadas anteriores
    setDeletedGalleryIds([]);

    setForm({

      id: "",
      nombre: "",
      ciudad: "",
      direccion: "",
      categoria: "",
      subcategoria: "",
      descripcion: "",
      foto: "",
      video: "",
      latitud: "",
      longitud: "",

    });

    setEditing(false);

    setFormOpen(true);

  }

  // =========================================================
  // EDITAR DESTINO
  // =========================================================

  function openEdit(destino) {

    console.log(
      "Destino para editar:",
      destino
    );

    console.log(
      "Imágenes:",
      destino.imagenes
    );

    setSelectedFile(null);

    setGalleryFiles([]);

    // MUY IMPORTANTE:
    // cada edición empieza sin imágenes marcadas
    // para eliminar
    setDeletedGalleryIds([]);

    setForm({

      id: destino.id ?? "",

      nombre:
        destino.nombre ?? "",

      ciudad:
        destino.ciudad ?? "",

      direccion:
        destino.direccion ?? "",

      categoria:
        destino.categoria ?? "",

      subcategoria:
        destino.subcategoria ?? "",

      descripcion:
        destino.descripcion ?? "",

      foto:
        destino.foto ?? "",

      video:
        destino.video ?? "",

      latitud:
        destino.latitud ?? "",

      longitud:
        destino.longitud ?? "",

    });

    // Cargar imágenes existentes
    setGallery(
      destino.imagenes ?? []
    );

    setEditing(true);

    setFormOpen(true);

  }

  // =========================================================
  // AGREGAR IMÁGENES A LA GALERÍA
  // =========================================================

  function handleGalleryChange(e) {

    const files = [
      ...e.target.files
    ];

    console.log(
      "Archivos seleccionados:",
      files
    );

    if (!files.length) {
      return;
    }

    // Crear objetos para las imágenes nuevas
    const nuevas = files.map((file) => ({

      // ID temporal para React
      id:
        `new-${Date.now()}-${Math.random()}`,

      // Archivo físico
      file,

      // Vista previa
      imagen:
        URL.createObjectURL(file),

      // Indica que todavía NO existe en BD
      nueva: true,

    }));

    console.log(
      "Nuevas imágenes:",
      nuevas
    );

    // =====================================================
    // GUARDAR ARCHIVOS NUEVOS
    // =====================================================

    setGalleryFiles((prev) => [

      ...prev,

      ...nuevas,

    ]);

    // =====================================================
    // MOSTRARLOS EN GALERÍA
    // =====================================================

    setGallery((prev) => [

      ...prev,

      ...nuevas,

    ]);

    // Limpiar input para permitir seleccionar
    // nuevamente el mismo archivo
    e.target.value = "";

  }

  // =========================================================
  // ELIMINAR IMAGEN DE GALERÍA
  // =========================================================

  function handleDeleteGalleryImage(img) {

    console.log(
      "Imagen que se quiere eliminar:",
      img
    );

    // =====================================================
    // CASO 1:
    // IMAGEN QUE YA EXISTE EN LA BASE DE DATOS
    // =====================================================

    if (!img.nueva) {

      console.log(
        "Imagen existente. ID:",
        img.id
      );

      setDeletedGalleryIds((prev) => {

        // Evitar IDs duplicados
        if (prev.includes(img.id)) {
          return prev;
        }

        return [
          ...prev,
          img.id,
        ];

      });

    }

    // =====================================================
    // CASO 2:
    // IMAGEN NUEVA QUE TODAVÍA NO SE HA GUARDADO
    // =====================================================

    else {

      console.log(
        "Imagen nueva. No se enviará al servidor."
      );

      setGalleryFiles((prev) =>

        prev.filter(
          (file) =>
            file.id !== img.id
        )

      );

      // Liberar URL temporal
      if (img.imagen) {

        URL.revokeObjectURL(
          img.imagen
        );

      }

    }

    // =====================================================
    // QUITAR DE LA VISTA
    // =====================================================

    setGallery((prev) =>

      prev.filter(
        (g) =>
          g.id !== img.id
      )

    );

  }

  // =========================================================
  // GUARDAR DESTINO
  // =========================================================

  async function handleSubmit(e) {

    e.preventDefault();

    const formData = new FormData();

    // =====================================================
    // DATOS DEL FORMULARIO
    // =====================================================

    Object.keys(form).forEach((key) => {

      if (
        form[key] !== null &&
        form[key] !== undefined
      ) {

        formData.append(
          key,
          form[key]
        );

      }

    });

    // =====================================================
    // FOTO PRINCIPAL
    // =====================================================

    if (selectedFile) {

      formData.append(
        "foto_file",
        selectedFile
      );

    }

    // =====================================================
    // IMÁGENES NUEVAS DE GALERÍA
    // =====================================================

    galleryFiles.forEach((item) => {

      formData.append(
        "imagenes[]",
        item.file
      );

    });

    // =====================================================
    // IMÁGENES EXISTENTES ELIMINADAS
    // =====================================================

    deletedGalleryIds.forEach((id) => {

      formData.append(
        "deleted_gallery_ids[]",
        id
      );

    });

    // =====================================================
    // DEBUG
    // =====================================================

    console.log(
      "================================="
    );

    console.log(
      "IMÁGENES NUEVAS:",
      galleryFiles
    );

    console.log(
      "IMÁGENES ELIMINADAS:",
      deletedGalleryIds
    );

    console.log(
      "================================="
    );

    for (
      let pair of formData.entries()
    ) {

      console.log(
        pair[0],
        pair[1]
      );

    }

    // =====================================================
    // CONFIGURACIÓN
    // =====================================================

    try {

      const config = {

        headers: {
          "Content-Type":
            "multipart/form-data",
        },

      };

      // ===================================================
      // EDITAR
      // ===================================================

      if (editing) {

        formData.append(
          "_method",
          "PUT"
        );

        await axios.post(
          `/destinos/${form.id}`,
          formData,
          config
        );

      }

      // ===================================================
      // CREAR
      // ===================================================

      else {

        await axios.post(
          "/destinos",
          formData,
          config
        );

      }

      // ===================================================
      // NOTIFICACIÓN
      // ===================================================

      setNotification({

        open: true,

        message:
          "¡Guardado exitosamente!",

        severity:
          "success",

      });

      // ===================================================
      // ACTUALIZAR LISTA
      // ===================================================

      await fetchDestinos();

      // ===================================================
      // CERRAR FORMULARIO
      // ===================================================

      setFormOpen(false);

      // ===================================================
      // LIMPIAR ESTADOS
      // ===================================================

      setSelectedFile(null);

      setGalleryFiles([]);

      setDeletedGalleryIds([]);

    } catch (err) {

      console.error(
        "Error al guardar:",
        err
      );

      console.log(
        "Respuesta del servidor:",
        err.response
      );

      setNotification({

        open: true,

        message:
          "Error al guardar",

        severity:
          "error",

      });

    }

  }

  // =========================================================
  // ELIMINAR DESTINO
  // =========================================================

  function openDelete(id) {

    setCurrentIdToDelete(id);

    setDeleteOpen(true);

  }

  async function handleConfirmDelete() {

    try {

      await axios.delete(
        `/destinos/${currentIdToDelete}`
      );

      setNotification({

        open: true,

        message:
          "Destino eliminado correctamente",

        severity:
          "success",

      });

      await fetchDestinos();

      setDeleteOpen(false);

    } catch (err) {

      console.error(
        "Error al eliminar destino:",
        err
      );

      setNotification({

        open: true,

        message:
          "Error al eliminar",

        severity:
          "error",

      });

    }

  }

  // =========================================================
  // BÚSQUEDA
  // =========================================================

  function handleSearch(e) {

    const q =
      e.target.value;

    setSearch(q);

    const ql =
      q.trim().toLowerCase();

    if (!ql) {

      setFiltered(
        destinos
      );

      return;

    }

    const result =
      destinos.filter(
        (d) =>

          String(
            d.nombre || ""
          )
            .toLowerCase()
            .includes(ql) ||

          String(
            d.ciudad || ""
          )
            .toLowerCase()
            .includes(ql) ||

          String(
            d.categoria || ""
          )
            .toLowerCase()
            .includes(ql)
      );

    setFiltered(result);

    setPage(0);

  }

  // =========================================================
  // PAGINACIÓN
  // =========================================================

  const rowsToShow =
    filtered.slice(
      page * rowsPerPage,
      page * rowsPerPage +
        rowsPerPage
    );

  // =========================================================
  // RENDER
  // =========================================================

  return (

    <ThemeProvider theme={theme}>

      <Box
        sx={{
          p: 3,
          bgcolor:
            "background.default",
          minHeight:
            "100vh",
        }}
      >

        {/* ===================================================
            LISTA DE DESTINOS
        ==================================================== */}

        <Card
          sx={{
            borderRadius: 3,
          }}
        >

          <CardHeader

            title={
              <Typography
                variant="h4"
              >
                Destinos
              </Typography>
            }

            action={

              <Fab
                color="primary"
                size="small"
                onClick={openNew}
              >
                <AddIcon />
              </Fab>

            }

          />

          <CardContent>

            {/* ================================
                BÚSQUEDA
            ================================= */}

            <Grid
              container
              spacing={2}
              alignItems="center"
              sx={{
                mb: 3,
              }}
            >

              <Grid
                item
                xs={12}
                md={6}
              >

                <TextField

                  value={search}

                  onChange={
                    handleSearch
                  }

                  fullWidth

                  placeholder="Buscar por nombre, ciudad o categoría..."

                  size="small"

                  InputProps={{

                    startAdornment: (

                      <InputAdornment
                        position="start"
                      >

                        <SearchIcon
                          color="action"
                        />

                      </InputAdornment>

                    ),

                  }}

                />

              </Grid>

              <Grid
                item
                xs={12}
                md={6}
              >

                <Stack
                  direction="row"
                  justifyContent={{
                    xs: "flex-start",
                    md: "flex-end",
                  }}
                >

                  <Button

                    variant="outlined"

                    size="small"

                    onClick={() => {

                      setSearch("");

                      setFiltered(
                        destinos
                      );

                    }}

                  >
                    Limpiar Filtros
                  </Button>

                </Stack>

              </Grid>

            </Grid>

            {/* ================================
                TABLA
            ================================= */}

            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
              }}
            >

              <Table
                size="small"
              >

                <TableHead
                  sx={{
                    bgcolor:
                      "#f5f7f9",
                  }}
                >

                  <TableRow>

                    <TableCell>
                      <b>
                        Nombre / Foto
                      </b>
                    </TableCell>

                    <TableCell>
                      <b>
                        Ciudad
                      </b>
                    </TableCell>

                    <TableCell>
                      <b>
                        Categoría
                      </b>
                    </TableCell>

                    <TableCell>
                      <b>
                        Descripción
                      </b>
                    </TableCell>

                    <TableCell align="center">
                      <b>
                        Video
                      </b>
                    </TableCell>

                    <TableCell align="center">
                      <b>
                        Acciones
                      </b>
                    </TableCell>

                  </TableRow>

                </TableHead>

                <TableBody>

                  {rowsToShow.map(
                    (destino) => (

                      <TableRow
                        key={
                          destino.id
                        }
                        hover
                      >

                        <TableCell>

                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >

                            <Avatar
                              variant="rounded"
                              src={
                                destino.foto
                              }
                              sx={{
                                width: 48,
                                height: 48,
                              }}
                            >

                              <ImageIcon />

                            </Avatar>

                            <Box>

                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 600,
                                }}
                              >
                                {
                                  destino.nombre
                                }
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {
                                  destino.direccion
                                }
                              </Typography>

                            </Box>

                          </Stack>

                        </TableCell>

                        <TableCell>
                          {
                            destino.ciudad
                          }
                        </TableCell>

                        <TableCell>
                          {
                            destino.categoria
                          }
                        </TableCell>

                        <TableCell
                          sx={{
                            maxWidth: 220,
                            whiteSpace:
                              "nowrap",
                            overflow:
                              "hidden",
                            textOverflow:
                              "ellipsis",
                          }}
                        >
                          {
                            destino.descripcion
                          }
                        </TableCell>

                        <TableCell
                          align="center"
                        >

                          {destino.video && (

                            <IconButton
                              size="small"
                              href={
                                destino.video
                              }
                              target="_blank"
                            >

                              <PlayIcon
                                color="primary"
                              />

                            </IconButton>

                          )}

                        </TableCell>

                        <TableCell
                          align="center"
                        >

                          <Stack
                            direction="row"
                            justifyContent="center"
                            spacing={1}
                          >

                            <IconButton
                              size="small"
                              onClick={() =>
                                openEdit(
                                  destino
                                )
                              }
                            >

                              <EditIcon
                                fontSize="small"
                              />

                            </IconButton>

                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                openDelete(
                                  destino.id
                                )
                              }
                            >

                              <DeleteIcon
                                fontSize="small"
                              />

                            </IconButton>

                          </Stack>

                        </TableCell>

                      </TableRow>

                    )
                  )}

                </TableBody>

              </Table>

              <TablePagination

                component="div"

                count={
                  filtered.length
                }

                page={page}

                onPageChange={(
                  e,
                  p
                ) =>
                  setPage(p)
                }

                rowsPerPage={
                  rowsPerPage
                }

                onRowsPerPageChange={(
                  e
                ) => {

                  setRowsPerPage(
                    parseInt(
                      e.target.value,
                      10
                    )
                  );

                  setPage(0);

                }}

                labelRowsPerPage="Filas:"

              />

            </TableContainer>

          </CardContent>

        </Card>

        {/* ===================================================
            DIÁLOGO FORMULARIO
        ==================================================== */}

        <Dialog

          open={formOpen}

          fullWidth

          maxWidth="lg"

          onClose={() =>
            setFormOpen(false)
          }

          PaperProps={{
            sx: {
              borderRadius: 4,
              overflow: "hidden",
              boxShadow:
                "0 20px 60px rgba(0,0,0,0.25)",
            },
          }}

        >

          {/* ============================
              CABECERA
          ============================= */}

          <DialogTitle

            sx={{

              px: {
                xs: 2,
                sm: 4,
              },

              py: 2.5,

              background:
                "linear-gradient(135deg, #0B74DA 0%, #0756A8 100%)",

              color: "#fff",

              display: "flex",

              alignItems: "center",

              justifyContent:
                "space-between",

            }}

          >

            <Box>

              <Typography

                variant="h5"

                sx={{

                  fontWeight: 700,

                  fontSize: {
                    xs: "1.2rem",
                    sm: "1.5rem",
                  },

                }}

              >

                {
                  editing
                    ? "Editar destino"
                    : "Nuevo destino"
                }

              </Typography>

              <Typography

                variant="body2"

                sx={{
                  opacity: 0.85,
                  mt: 0.5,
                }}

              >

                {
                  editing
                    ? "Actualiza la información del destino turístico"
                    : "Registra un nuevo destino turístico"
                }

              </Typography>

            </Box>

            <IconButton

              onClick={() =>
                setFormOpen(false)
              }

              sx={{

                color: "#fff",

                backgroundColor:
                  "rgba(255,255,255,0.12)",

                "&:hover": {
                  backgroundColor:
                    "rgba(255,255,255,0.22)",
                },

              }}

            >

              <CloseIcon />

            </IconButton>

          </DialogTitle>

          {/* ============================
              CONTENIDO
          ============================= */}

          <DialogContent

            dividers

            sx={{

              backgroundColor:
                "#F7F9FC",

              px: {
                xs: 2,
                sm: 4,
              },

              py: 3,

            }}

          >

            <Grid
              container
              spacing={3}
            >

              {/* =================================================
                  INFORMACIÓN BÁSICA
              ================================================== */}

              <Grid
                item
                xs={12}
              >

                <Box
                  sx={{

                    backgroundColor:
                      "#fff",

                    borderRadius: 3,

                    p: {
                      xs: 2,
                      sm: 3,
                    },

                    border:
                      "1px solid #E5EAF0",

                  }}
                >

                  <Box
                    sx={{
                      mb: 2.5,
                    }}
                  >

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#17324D",
                      }}
                    >
                      Información básica
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Datos principales del destino turístico.
                    </Typography>

                  </Box>

                  <Grid
                    container
                    spacing={2}
                  >

                    <Grid
                      item
                      xs={12}
                      md={6}
                    >

                      <TextField

                        name="nombre"

                        label="Nombre del destino"

                        value={
                          form.nombre
                        }

                        onChange={
                          handleInputChange
                        }

                        fullWidth

                        required

                        placeholder="Ej. Plaza de Armas"

                      />

                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={3}
                    >

                      <TextField

                        name="ciudad"

                        label="Ciudad"

                        value={
                          form.ciudad
                        }

                        onChange={
                          handleInputChange
                        }

                        fullWidth

                        required

                        placeholder="Ej. Juliaca"

                      />

                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={3}
                    >

                      <TextField

                        name="direccion"

                        label="Dirección"

                        value={
                          form.direccion
                        }

                        onChange={
                          handleInputChange
                        }

                        fullWidth

                        required

                        placeholder="Ej. Jr. 2 de Mayo"

                      />

                    </Grid>

                    {/* CATEGORÍA */}

                    <Grid
                      item
                      xs={12}
                      md={6}
                    >

                      <TextField

                        select

                        fullWidth

                        label="Categoría"

                        name="categoria"

                        value={
                          form.categoria ||
                          ""
                        }

                        onChange={(e) =>
                          setForm({

                            ...form,

                            categoria:
                              e.target.value,

                            subcategoria:
                              "",

                          })
                        }

                        SelectProps={{
                          displayEmpty:
                            true,
                        }}

                        sx={{

                          "& .MuiInputBase-root":
                            {
                              minHeight:
                                56,

                              borderRadius:
                                2,

                              backgroundColor:
                                "#fff",
                            },

                          "& .MuiInputLabel-root":
                            {

                              fontWeight:
                                500,

                              transform:
                                "translate(14px, -2px) scale(0.85)",

                            },

                          "& .MuiInputLabel-shrink":
                            {

                              transform:
                                "translate(14px, -2px) scale(0.85)",

                            },

                        }}

                      >

                        <MenuItem
                          value=""
                          disabled
                        >
                          Selecciona una categoría
                        </MenuItem>

                        {categoriaOptions.map(
                          (c) => (

                            <MenuItem
                              key={c}
                              value={c}
                            >
                              {c}
                            </MenuItem>

                          )
                        )}

                      </TextField>

                    </Grid>

                    {/* SUBCATEGORÍA */}

                    <Grid
                      item
                      xs={12}
                      md={6}
                    >

                      <TextField

                        select

                        fullWidth

                        label="Subcategoría"

                        name="subcategoria"

                        value={
                          form.subcategoria ||
                          ""
                        }

                        onChange={
                          handleInputChange
                        }

                        disabled={
                          !form.categoria
                        }

                        SelectProps={{
                          displayEmpty:
                            true,
                        }}

                        sx={{

                          "& .MuiInputBase-root":
                            {

                              minHeight:
                                56,

                              borderRadius:
                                2,

                              backgroundColor:
                                !form.categoria
                                  ? "#f5f5f5"
                                  : "#fff",

                            },

                          "& .MuiInputLabel-root":
                            {

                              fontWeight:
                                500,

                              transform:
                                "translate(14px, -2px) scale(0.85)",

                            },

                          "& .MuiInputLabel-shrink":
                            {

                              transform:
                                "translate(14px, -2px) scale(0.85)",

                            },

                        }}

                      >

                        <MenuItem
                          value=""
                          disabled
                        >

                          {form.categoria

                            ? "Selecciona una subcategoría"

                            : "Primero selecciona una categoría"}

                        </MenuItem>

                        {subcategoriaOptions.map(
                          (s) => (

                            <MenuItem
                              key={s}
                              value={s}
                            >
                              {s}
                            </MenuItem>

                          )
                        )}

                      </TextField>

                    </Grid>

                  </Grid>

                </Box>

              </Grid>

              {/* =================================================
                  IMAGEN PRINCIPAL
              ================================================== */}

              <Grid
                item
                xs={12}
                md={6}
              >

                <Box
                  sx={{

                    backgroundColor:
                      "#fff",

                    borderRadius: 3,

                    p: {
                      xs: 2,
                      sm: 3,
                    },

                    border:
                      "1px solid #E5EAF0",

                    height: "100%",

                  }}
                >

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#17324D",
                      mb: 0.5,
                    }}
                  >
                    Imagen principal
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                    }}
                  >
                    Esta será la imagen principal del destino.
                  </Typography>

                  {/* PREVISUALIZACIÓN */}

                  <Box
                    sx={{

                      width: "100%",

                      height: {
                        xs: 220,
                        sm: 260,
                      },

                      borderRadius: 3,

                      overflow:
                        "hidden",

                      backgroundColor:
                        "#EEF2F6",

                      border:
                        "1px dashed #B8C4D0",

                      mb: 2,

                      display: "flex",

                      alignItems:
                        "center",

                      justifyContent:
                        "center",

                    }}
                  >

                    {form.foto ? (

                      <img

                        src={
                          form.foto
                        }

                        alt="Vista previa"

                        style={{

                          width: "100%",

                          height: "100%",

                          objectFit:
                            "cover",

                        }}

                      />

                    ) : (

                      <Box
                        sx={{
                          textAlign:
                            "center",
                          color:
                            "text.secondary",
                        }}
                      >

                        <ImageIcon
                          sx={{
                            fontSize: 55,
                            opacity: 0.4,
                          }}
                        />

                        <Typography
                          variant="body2"
                        >
                          No hay imagen seleccionada
                        </Typography>

                      </Box>

                    )}

                  </Box>

                  <TextField

                    name="foto"

                    label="URL de imagen"

                    value={
                      form.foto
                    }

                    onChange={
                      handleInputChange
                    }

                    fullWidth

                    InputProps={{

                      endAdornment: (

                        <InputAdornment
                          position="end"
                        >

                          <input

                            accept="image/jpeg,image/png,image/jpg,image/webp"

                            style={{
                              display:
                                "none",
                            }}

                            id="upload-photo-file"

                            type="file"

                            onChange={
                              handleFileChange
                            }

                          />

                          <label
                            htmlFor="upload-photo-file"
                          >

                            <Tooltip
                              title="Seleccionar imagen"
                            >

                              <IconButton
                                color="primary"
                                component="span"
                              >
                                <UploadIcon />
                              </IconButton>

                            </Tooltip>

                          </label>

                        </InputAdornment>

                      ),

                    }}

                  />

                </Box>

              </Grid>

              {/* =================================================
                  GALERÍA
              ================================================== */}

              <Grid
                item
                xs={12}
                md={6}
              >

                <Box
                  sx={{

                    backgroundColor:
                      "#fff",

                    borderRadius: 3,

                    p: {
                      xs: 2,
                      sm: 3,
                    },

                    border:
                      "1px solid #E5EAF0",

                    height: "100%",

                  }}
                >

                  <Box
                    sx={{

                      display: "flex",

                      justifyContent:
                        "space-between",

                      alignItems:
                        "center",

                      mb: 0.5,

                    }}
                  >

                    <Box>

                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: "#17324D",
                        }}
                      >
                        Galería de imágenes
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        Agrega imágenes adicionales del destino.
                      </Typography>

                    </Box>

                    <Typography
                      variant="caption"
                      sx={{

                        backgroundColor:
                          "#E8F1FC",

                        color:
                          "#0B74DA",

                        px: 1.5,

                        py: 0.7,

                        borderRadius: 2,

                        fontWeight: 600,

                      }}
                    >

                      {
                        gallery.length
                      }{" "}
                      fotos

                    </Typography>

                  </Box>

                  {/* BOTÓN AGREGAR */}

                  <Button

                    variant="contained"

                    component="label"

                    startIcon={
                      <UploadIcon />
                    }

                    sx={{

                      mt: 2,

                      borderRadius: 2,

                      textTransform:
                        "none",

                      fontWeight: 600,

                      boxShadow:
                        "none",

                    }}

                  >

                    Agregar imágenes

                    <input

                      hidden

                      multiple

                      accept="image/jpeg,image/png,image/jpg,image/webp"

                      type="file"

                      onChange={
                        handleGalleryChange
                      }

                    />

                  </Button>

                  {/* GALERÍA */}

                  <Box
                    sx={{

                      mt: 2,

                      display: "grid",

                      gridTemplateColumns:
                        {

                          xs:
                            "repeat(2, 1fr)",

                          sm:
                            "repeat(3, 1fr)",

                        },

                      gap: 1.5,

                      maxHeight: 260,

                      overflowY:
                        "auto",

                      pr: 0.5,

                    }}
                  >

                    {gallery.length > 0 ? (

                      gallery.map(
                        (img) => (

                          <Box

                            key={
                              img.id
                            }

                            sx={{

                              position:
                                "relative",

                              height: 100,

                              borderRadius: 2,

                              overflow:
                                "hidden",

                              backgroundColor:
                                "#EEF2F6",

                              border:
                                "1px solid #E0E5EA",

                            }}

                          >

                            <img

                              src={
                                img.imagen
                              }

                              alt=""

                              style={{

                                width:
                                  "100%",

                                height:
                                  "100%",

                                objectFit:
                                  "cover",

                                display:
                                  "block",

                              }}

                            />

                            {/* BOTÓN ELIMINAR */}

                            <IconButton

                              size="small"

                              color="error"

                              sx={{

                                position:
                                  "absolute",

                                top: 5,

                                right: 5,

                                backgroundColor:
                                  "#fff",

                                width: 30,

                                height: 30,

                                boxShadow: 2,

                                "&:hover":
                                  {

                                    backgroundColor:
                                      "#fff",

                                  },

                              }}

                              onClick={() =>
                                handleDeleteGalleryImage(
                                  img
                                )
                              }

                            >

                              <DeleteIcon
                                fontSize="small"
                              />

                            </IconButton>

                          </Box>

                        )
                      )

                    ) : (

                      <Box
                        sx={{

                          gridColumn:
                            "1 / -1",

                          height: 150,

                          display:
                            "flex",

                          alignItems:
                            "center",

                          justifyContent:
                            "center",

                          flexDirection:
                            "column",

                          border:
                            "1px dashed #C5CDD5",

                          borderRadius:
                            3,

                          color:
                            "text.secondary",

                        }}
                      >

                        <ImageIcon
                          sx={{

                            fontSize:
                              42,

                            opacity:
                              0.35,

                            mb: 1,

                          }}
                        />

                        <Typography
                          variant="body2"
                        >
                          No hay imágenes en la galería
                        </Typography>

                      </Box>

                    )}

                  </Box>

                </Box>

              </Grid>

              {/* =================================================
                  MULTIMEDIA
              ================================================== */}

              <Grid
                item
                xs={12}
              >

                <Box
                  sx={{

                    backgroundColor:
                      "#fff",

                    borderRadius: 3,

                    p: {
                      xs: 2,
                      sm: 3,
                    },

                    border:
                      "1px solid #E5EAF0",

                  }}
                >

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#17324D",
                      mb: 0.5,
                    }}
                  >
                    Multimedia
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                    }}
                  >
                    Puedes agregar un enlace de YouTube, Google Drive u otro video.
                  </Typography>

                  <TextField

                    name="video"

                    label="URL del video"

                    value={
                      form.video
                    }

                    onChange={
                      handleInputChange
                    }

                    fullWidth

                    placeholder="https://www.youtube.com/..."

                    InputProps={{

                      startAdornment: (

                        <InputAdornment
                          position="start"
                        >

                          <PlayIcon
                            color="primary"
                          />

                        </InputAdornment>

                      ),

                    }}

                  />

                </Box>

              </Grid>

              {/* =================================================
                  UBICACIÓN
              ================================================== */}

              <Grid
                item
                xs={12}
              >

                <Box
                  sx={{

                    backgroundColor:
                      "#fff",

                    borderRadius: 3,

                    p: {
                      xs: 2,
                      sm: 3,
                    },

                    border:
                      "1px solid #E5EAF0",

                  }}
                >

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#17324D",
                      mb: 0.5,
                    }}
                  >
                    Ubicación
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                    }}
                  >
                    Ingresa las coordenadas geográficas del destino.
                  </Typography>

                  <Grid
                    container
                    spacing={2}
                  >

                    <Grid
                      item
                      xs={12}
                      sm={6}
                    >

                      <TextField

                        type="number"

                        name="latitud"

                        label="Latitud"

                        value={
                          form.latitud
                        }

                        onChange={
                          handleInputChange
                        }

                        fullWidth

                        placeholder="-15.489"

                      />

                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                    >

                      <TextField

                        type="number"

                        name="longitud"

                        label="Longitud"

                        value={
                          form.longitud
                        }

                        onChange={
                          handleInputChange
                        }

                        fullWidth

                        placeholder="-70.133"

                      />

                    </Grid>

                  </Grid>

                </Box>

              </Grid>

              {/* =================================================
                  DESCRIPCIÓN
              ================================================== */}

              <Grid
                item
                xs={12}
              >

                <Box
                  sx={{

                    backgroundColor:
                      "#fff",

                    borderRadius: 3,

                    p: {
                      xs: 2,
                      sm: 3,
                    },

                    border:
                      "1px solid #E5EAF0",

                  }}
                >

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#17324D",
                      mb: 0.5,
                    }}
                  >
                    Descripción
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                    }}
                  >
                    Describe las principales características del destino.
                  </Typography>

                  <TextField

                    name="descripcion"

                    label="Descripción del destino"

                    value={
                      form.descripcion
                    }

                    onChange={
                      handleInputChange
                    }

                    multiline

                    rows={5}

                    fullWidth

                    placeholder="Escribe una descripción del destino turístico..."

                  />

                </Box>

              </Grid>

            </Grid>

          </DialogContent>

          {/* ===================================================
              BOTONES
          ==================================================== */}

          <DialogActions

            sx={{

              px: {
                xs: 2,
                sm: 4,
              },

              py: 2,

              backgroundColor:
                "#fff",

              borderTop:
                "1px solid #E5EAF0",

              gap: 1,

            }}

          >

            <Button

              onClick={() =>
                setFormOpen(false)
              }

              color="inherit"

              variant="outlined"

              sx={{

                borderRadius: 2,

                px: 3,

                textTransform:
                  "none",

              }}

            >
              Cancelar
            </Button>

            <Button

              onClick={
                handleSubmit
              }

              variant="contained"

              sx={{

                borderRadius: 2,

                px: 4,

                textTransform:
                  "none",

                fontWeight: 600,

                boxShadow:
                  "0 4px 12px rgba(11,116,218,0.25)",

              }}

            >

              {
                editing
                  ? "Guardar cambios"
                  : "Crear destino"
              }

            </Button>

          </DialogActions>

        </Dialog>

        {/* ===================================================
            NOTIFICACIÓN
        ==================================================== */}

        <Snackbar

          open={
            notification.open
          }

          autoHideDuration={5000}

          onClose={
            handleCloseNotification
          }

          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}

        >

          <Alert

            onClose={
              handleCloseNotification
            }

            severity={
              notification.severity
            }

            variant="filled"

            sx={{
              width: "100%",
            }}

          >

            {
              notification.message
            }

          </Alert>

        </Snackbar>

        {/* ===================================================
            CONFIRMAR ELIMINACIÓN DESTINO
        ==================================================== */}

        <Dialog

          open={deleteOpen}

          onClose={() =>
            setDeleteOpen(false)
          }

        >

          <DialogTitle>
            ¿Confirmar eliminación?
          </DialogTitle>

          <DialogContent>

            <Typography>
              Esta acción borrará permanentemente el destino.
            </Typography>

          </DialogContent>

          <DialogActions>

            <Button
              onClick={() =>
                setDeleteOpen(false)
              }
            >
              Cancelar
            </Button>

            <Button

              onClick={
                handleConfirmDelete
              }

              color="error"

              variant="contained"

            >
              Eliminar
            </Button>

          </DialogActions>

        </Dialog>

      </Box>

    </ThemeProvider>

  );

}